import React, { useState } from 'react';
import CustomSelector from '@shared/components/CustomSelector';
import { Button, Dialog } from '@mui/material';
import { LICENSE_CHANGE_VIEWS } from '../../../constants/LicenseChangeViews';
import { PAGE_MODE } from '../../../constants/pageModeTypes';
import { useReactiveVar } from '@apollo/client';
import { userPrivileges } from '@apollo-internal/cache';
import PrivilegeNames from '../../../../../constants/privilegeNames';
import ActivateLicense from './ActivateLicense';
import DeactivateLicense from './DeactivateLicense';
import MoveLicenseToIsaca from './MoveLicenseToIsaca';
import MoveLicenseToOrgUnit from './MoveLicenseToOrgUnit';
import LicenseEditDialog from './LicenseEditDialog';
import * as LicenseTypes from '../../../constants/LicenseTypes';
import MoveLicenseToFree from './MoveLicenseToFree';
import MoveLicenseToPaid from './MoveLicenseToPaid';
import MoveLicenseToOrg from './MoveLicenseToOrg';

const CHANGE_LICENSE_TEXT = 'Change License';
const TYPE_OF_CHANGE_TEXT = 'Type of Change';
const TYPE_OF_CHANGE_PLACEHOLDER = 'Select a Type of Change';

const BASE_ACTION_OPTIONS = [
  {
    title: 'Activate',
    value: LICENSE_CHANGE_VIEWS.ACTIVATE,
    condition: isUserDeactivated => isUserDeactivated
  },
  {
    title: 'Deactivate',
    value: LICENSE_CHANGE_VIEWS.DEACTIVATE,
    condition: isUserDeactivated => !isUserDeactivated
  },
  {
    title: 'Move to ISACA',
    value: LICENSE_CHANGE_VIEWS.MOVE_TO_ISACA,
    condition: (_, pageMode, privileges) =>
      (pageMode === PAGE_MODE.MddapOrg || pageMode === PAGE_MODE.OrgUnit) &&
      privileges.includes(PrivilegeNames.UserManagement_ChangeLicense_MoveToOrg)
  },
  {
    title: 'Move To Org',
    value: LICENSE_CHANGE_VIEWS.MOVE_TO_ORG,
    condition: (_, pageMode, privileges, licenseType) =>
      licenseType === LicenseTypes.PAID.value &&
      (pageMode === PAGE_MODE.MddapOrg || pageMode === PAGE_MODE.OrgUnit) &&
      privileges.includes(PrivilegeNames.UserManagement_ChangeLicense_MoveToOrg)
  },
  {
    title: 'Free',
    value: LICENSE_CHANGE_VIEWS.FREE,
    condition: (_, pageMode, privileges, licenseType) =>
      licenseType === LicenseTypes.PAID.value &&
      (pageMode === PAGE_MODE.MddapOrg ||
        pageMode === PAGE_MODE.MDICVipOrg ||
        pageMode === PAGE_MODE.OrgUnit) &&
      privileges.includes(PrivilegeNames.UserManagement_ChangeLicense_ChangeUserFreeStatus)
  },
  {
    title: 'Paid',
    value: LICENSE_CHANGE_VIEWS.PAID,
    condition: (_, pageMode, privileges, licenseType) =>
      licenseType === LicenseTypes.FREE.value &&
      (pageMode === PAGE_MODE.MddapOrg || pageMode === PAGE_MODE.OrgUnit) &&
      privileges.includes(PrivilegeNames.UserManagement_ChangeLicense_ChangeUserFreeStatus)
  },
  {
    title: 'Change Sponsoring Partner',
    value: LICENSE_CHANGE_VIEWS.CHANGE_SPONSORING_PARTNER,
    condition: (_, pageMode, privileges) =>
      pageMode === PAGE_MODE.ISACAAppraiser &&
      privileges.includes(
        PrivilegeNames.UserManagement_ChangeLicense_MoveLicenseIsacaOrgUnitToIsacaOrgUnit
      )
  }
];

/**
 * @typedef {object} ChangeLicenseOwnProps
 * @property {boolean} [isUserDeactivated]
 * @property {number} [pageMode]
 * @property {string} [previousLink]
 * @property {boolean} [isFree]
 */

/**
 * @typedef {ChangeLicenseOwnProps &
 *   (
 *     | React.ComponentProps<typeof ActivateLicense>
 *     | React.ComponentProps<typeof DeactivateLicense>
 *     | React.ComponentProps<typeof MoveLicenseToIsaca>
 *     | React.ComponentProps<typeof MoveLicenseToFree>
 *     | React.ComponentProps<typeof MoveLicenseToPaid>
 *     | React.ComponentProps<typeof MoveLicenseToOrg>
 *     | React.ComponentProps<typeof MoveLicenseToOrgUnit>
 *     | React.ComponentProps<typeof LicenseEditDialog>
 *   )} ChangeLicenseProps
 */

/**
 * @param {ChangeLicenseProps} props
 */
const ChangeLicense = props => {
  const { isUserDeactivated, pageMode, previousLink, isFree } = props;
  const [openModal, setOpenModal] = useState(false);
  const [selectedView, setSelectedView] = useState(null);
  const previousLinkWithActiveTab = `${previousLink}#${getLicenseType(isUserDeactivated, isFree)}`;

  const privileges = useReactiveVar(userPrivileges);
  const filteredActionsOptions = getActionOptions(
    isUserDeactivated,
    pageMode,
    privileges,
    getLicenseType(isUserDeactivated, isFree)
  );

  const handleClose = () => {
    setOpenModal(false);
  };

  const handleOpen = () => {
    setOpenModal(true);
  };

  const licenseViewProps = {
    ...props,
    dialogContent: (
      <CustomSelector
        id='type-of-change-selector'
        title={TYPE_OF_CHANGE_TEXT}
        placeholder={TYPE_OF_CHANGE_PLACEHOLDER}
        testId='type-of-change-selector'
        value={selectedView}
        setValue={setSelectedView}
        options={filteredActionsOptions}
        menuProps={{ disablePortal: true }}
      />
    ),
    dialogActions: (
      <Button className='secondary-button sm' onClick={handleClose}>
        Cancel
      </Button>
    ),
    previousLink: previousLinkWithActiveTab
  };

  return (
    <div className='change-license-popup-container'>
      <Button className='primary-button sm' onClick={handleOpen}>
        {CHANGE_LICENSE_TEXT}
      </Button>
      <Dialog
        open={openModal}
        onClose={handleClose}
        className='change-license-popup'
        fullWidth={true}
        maxWidth='md'
        aria-labelledby='alert-dialog-title'
        aria-describedby='alert-dialog-description'
        slotProps={{
          transition: {
            onExited: () => setSelectedView(null)
          }
        }}>
        {renderSelectedLicenseView(selectedView, licenseViewProps)}
      </Dialog>
    </div>
  );
};

const renderSelectedLicenseView = (view, viewProps) => {
  switch (view) {
    case LICENSE_CHANGE_VIEWS.ACTIVATE:
      return <ActivateLicense {...viewProps} />;
    case LICENSE_CHANGE_VIEWS.DEACTIVATE:
      return <DeactivateLicense {...viewProps} />;
    case LICENSE_CHANGE_VIEWS.MOVE_TO_ISACA:
      return <MoveLicenseToIsaca {...viewProps} />;
    case LICENSE_CHANGE_VIEWS.FREE:
      return <MoveLicenseToFree {...viewProps} />;
    case LICENSE_CHANGE_VIEWS.PAID:
      return <MoveLicenseToPaid {...viewProps} />;
    case LICENSE_CHANGE_VIEWS.MOVE_TO_ORG:
      return <MoveLicenseToOrg {...viewProps} />;
    case LICENSE_CHANGE_VIEWS.CHANGE_SPONSORING_PARTNER:
      return <MoveLicenseToOrgUnit {...viewProps} />;
    default:
      return <LicenseEditDialog {...viewProps} />;
  }
};

const getActionOptions = (isUserDeactivated, pageMode, privileges, licenseType) =>
  BASE_ACTION_OPTIONS.filter(option =>
    option.condition(isUserDeactivated, pageMode, privileges, licenseType)
  );

const getLicenseType = (isUserDeactivated, isFree) => {
  if (isUserDeactivated) {
    return LicenseTypes.DEACTIVATED.value;
  }

  if (isFree) {
    return LicenseTypes.FREE.value;
  }

  return LicenseTypes.PAID.value;
};

export default ChangeLicense;
